import { useTranslations } from "next-intl";
import { Card } from "../Card/Card";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { SecondaryButton } from "../Buttons/SecondaryButton";
import { PrimaryButton } from "../Buttons/PrimaryButton";
import { User } from "@/types/User";
import { UserDTO } from "@/types/UserDTO";
import { updateUser } from "@/lib/api/handlers/UserHandling";
import { IconX } from "@tabler/icons-react";

type PEditUserColorProps = {
  onClose: () => void;
  selectedColor: string;
  setUserState: React.Dispatch<React.SetStateAction<User | UserDTO>>;
  initial: string;
};

// Color lookup object
const colorClasses = {
  "1": { bg: "user-icon-bg-1", font: "user-icon-font-1" },
  "2": { bg: "user-icon-bg-2", font: "user-icon-font-2" },
  "3": { bg: "user-icon-bg-3", font: "user-icon-font-3" },
  "4": { bg: "user-icon-bg-4", font: "user-icon-font-4" },
  "5": { bg: "user-icon-bg-5", font: "user-icon-font-5" },
  "6": { bg: "user-icon-bg-6", font: "user-icon-font-6" },
  "7": { bg: "user-icon-bg-7", font: "user-icon-font-7" },
  "8": { bg: "user-icon-bg-8", font: "user-icon-font-8" },
  "9": { bg: "user-icon-bg-9", font: "user-icon-font-9" },
  "10": { bg: "user-icon-bg-10", font: "user-icon-font-10" },
  "11": { bg: "user-icon-bg-11", font: "user-icon-font-11" },
  "12": { bg: "user-icon-bg-12", font: "user-icon-font-12" },
  "13": { bg: "user-icon-bg-13", font: "user-icon-font-13" },
  "14": { bg: "user-icon-bg-14", font: "user-icon-font-14" },
  "15": { bg: "user-icon-bg-15", font: "user-icon-font-15" },
  "16": { bg: "user-icon-bg-16", font: "user-icon-font-16" },
  "17": { bg: "user-icon-bg-17", font: "user-icon-font-17" },
  "18": { bg: "user-icon-bg-18", font: "user-icon-font-18" },
  "19": { bg: "user-icon-bg-19", font: "user-icon-font-19" },
  "20": { bg: "user-icon-bg-20", font: "user-icon-font-20" },
} as const;

export const PEditUserColor: React.FC<PEditUserColorProps> = ({
  onClose,
  selectedColor: selectedColorProp,
  setUserState,
  initial,
}) => {
  const t = useTranslations("pEditUserColor");
  const [mounted, setMounted] = useState(false);
  const [selectedColor, setSelectedColor] = useState<string>(selectedColorProp);
  const [loading, setLoading] = useState(false);

  const colors = [
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "10",
    "11",
    "12",
    "13",
    "14",
    "15",
    "16",
    "17",
    "18",
    "19",
    "20",
  ] as const;

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const modalContent = (
    <motion.div
      data-modal="true"
      className="fixed left-0 top-0 bg-black/50 flex items-center justify-center sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none h-screen w-screen z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          e.stopPropagation();
          onClose();
        }
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ type: "spring", damping: 20, stiffness: 300 }}
      >
        <Card
          width="w-[95vw] sm:max-w-110"
          padding="p-8"
          justify="justify-center items-center gap-8"
          header={t("editPicture")}
          icon={IconX}
          iconActionOnClick={onClose}
        >
          <div className="w-full space-y-6">
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {colors.map((color) => (
                <button
                  key={color}
                  onClick={() => setSelectedColor(color)}
                  className={`w-16 h-16 rounded-full ${
                    colorClasses[color].bg
                  } ${selectedColor === color ? "border-4 outline-focus" : ""}`}
                >
                  <p className={`h1 ${colorClasses[color].font}`}>
                    {typeof initial === "string" && initial.length > 0
                      ? initial.charAt(0).toUpperCase()
                      : "X"}
                  </p>
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-row w-full gap-3 pt-2">
            <SecondaryButton
              width="flex-1"
              onClick={() => {
                onClose();
              }}
              text={t("cancel")}
            />
            <PrimaryButton
              width="flex-1"
              text={t("save")}
              loading={loading}
              disabled={selectedColor === selectedColorProp}
              onClick={async () => {
                setLoading(true);
                try {
                  await updateUser({
                    monogramColor: selectedColor,
                  });
                  setUserState((prev) => ({
                    ...prev,
                    monogramColor: selectedColor,
                  }));
                } catch {
                  // error is handled silently; button re-enables
                } finally {
                  setLoading(false);
                }
              }}
            />
          </div>
        </Card>
      </motion.div>
    </motion.div>
  );

  if (!mounted) return null;

  return createPortal(modalContent, document.body);
};
